import { useCallback, useEffect, useMemo, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { useAuth } from "@/context/AuthContext";
import { listEvents, createEvent, updateEvent, deleteEvent, eventClashes } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Globe2, Building2, MapPin,
  List, AlertTriangle, Pencil, Trash2, LayoutGrid, CalendarRange,
} from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SCOPE_LABEL = { pan_liftr: "Pan LIFTR", city: "City", facility: "Facility" };
const SCOPE_CHIP = {
  pan_liftr: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  city: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  facility: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

// ---- date helpers (pure) ----
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function monthCells(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

function weekDays(anchorISO) {
  const a = new Date(anchorISO + "T00:00:00");
  const offset = (a.getDay() + 6) % 7;
  const monday = new Date(a.getFullYear(), a.getMonth(), a.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
}

const groupByDate = (events) => {
  const map = {};
  for (const e of events) (map[e.date] ||= []).push(e);
  for (const k of Object.keys(map)) map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  return map;
};

// ================= main page =================
export default function Events() {
  const { activeFacility } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [view, setView] = useState("month");           // month | week | agenda
  const [scope, setScope] = useState("facility");      // facility | global
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);        // event being edited or null
  const [detailEvent, setDetailEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEvents(scope, year, month + 1);
      setEvents(res.events || []);
    } catch {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  }, [scope, year, month]);

  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const shift = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const byDate = useMemo(() => groupByDate(events), [events]);
  const cells = useMemo(() => monthCells(year, month), [year, month]);
  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const openCreate = (dateISO) => {
    setEditing({ __new: true, date: dateISO || toISO(now), scope: "facility" });
    setDialogOpen(true);
  };
  const openEdit = (ev) => {
    setEditing(ev);
    setDialogOpen(true);
  };

  const clashCount = events.filter((e) => (e.clashes || []).length > 0).length;

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Retention · Events</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Events Calendar</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Pan-LIFTR, city-wide and gym-specific events in one place. Overlaps with pan-city events are flagged so
            your gym never double-books against a LIFTR-wide day.
          </p>
        </div>
        <Button data-testid="add-event-btn" onClick={() => openCreate()}>
          <Plus className="h-4 w-4 mr-1.5" /> New event
        </Button>
      </div>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* scope toggle */}
        <div className="inline-flex rounded-lg border border-[#262626] bg-[#0d0d0d] p-0.5" data-testid="scope-toggle">
          {[
            { v: "facility", label: activeFacility?.name || "This facility", icon: Building2, tid: "scope-facility" },
            { v: "global", label: "Global (all LIFTR)", icon: Globe2, tid: "scope-global" },
          ].map(({ v, label, icon: Icon, tid }) => (
            <button
              key={v}
              data-testid={tid}
              onClick={() => setScope(v)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                scope === v ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* view toggle */}
        <div className="inline-flex rounded-lg border border-[#262626] bg-[#0d0d0d] p-0.5" data-testid="view-toggle">
          {[
            { v: "month", icon: LayoutGrid, label: "Month", tid: "view-month" },
            { v: "week", icon: CalendarRange, label: "Week", tid: "view-week" },
            { v: "agenda", icon: List, label: "Agenda", tid: "view-agenda" },
          ].map(({ v, icon: Icon, label, tid }) => (
            <button
              key={v}
              data-testid={tid}
              onClick={() => setView(v)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                view === v ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* month nav */}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="icon" data-testid="prev-month" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[150px] text-center font-display font-bold" data-testid="month-label">{monthLabel}</div>
          <Button variant="outline" size="icon" data-testid="next-month" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" data-testid="today-btn" onClick={goToday}>Today</Button>
        </div>
      </div>

      {/* clash banner */}
      {clashCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-2.5 text-sm text-amber-300" data-testid="clash-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {clashCount} event{clashCount === 1 ? "" : "s"} overlap{clashCount === 1 ? "s" : ""} with a pan-city / pan-LIFTR event this month.
        </div>
      )}

      {/* legend */}
      <div className="flex items-center gap-4 text-[11px] text-zinc-500" data-testid="events-legend">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-400" /> Pan LIFTR</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sky-400" /> City-wide</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" /> This gym</span>
        {loading && <span className="ml-auto text-zinc-600">Loading…</span>}
      </div>

      {/* body */}
      {view === "month" && (
        <MonthGrid year={year} month={month} cells={cells} byDate={byDate} onDayClick={openCreate} onEventClick={setDetailEvent} />
      )}
      {view === "week" && (
        <WeekView byDate={byDate} onEventClick={setDetailEvent} />
      )}
      {view === "agenda" && (
        <AgendaView events={events} onEventClick={setDetailEvent} />
      )}

      {/* create/edit dialog */}
      <EventDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        initial={editing}
        activeFacility={activeFacility}
        onSaved={() => { setDialogOpen(false); setEditing(null); load(); }}
      />

      {/* detail dialog */}
      <EventDetail
        event={detailEvent}
        onClose={() => setDetailEvent(null)}
        onEdit={(ev) => { setDetailEvent(null); openEdit(ev); }}
        onDeleted={() => { setDetailEvent(null); load(); }}
      />
    </div>
  );
}

// ================= month grid =================
function MonthGrid({ year, month, cells, byDate, onDayClick, onEventClick }) {
  const todayISO = toISO(new Date());
  return (
    <div className="card-surface overflow-hidden" data-testid="month-grid">
      <div className="grid grid-cols-7 border-b border-[#1f1f1f] bg-[#0d0d0d]">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-3 py-2 text-[10px] font-medium uppercase tracking-[0.18em] text-zinc-500">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {(cells || []).map((d) => {
          const iso = toISO(d);
          const inMonth = d.getMonth() === month;
          const evts = byDate[iso] || [];
          return (
            <div
              key={iso}
              data-testid={`day-${iso}`}
              onClick={() => onDayClick(iso)}
              className={`min-h-[104px] cursor-pointer border-b border-r border-[#161616] p-1.5 transition-colors hover:bg-[#131313] ${
                inMonth ? "" : "opacity-35"
              }`}
            >
              <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                iso === todayISO ? "bg-emerald-500 font-bold text-black" : "text-zinc-400"
              }`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {evts.slice(0, 3).map((e) => (
                  <button
                    key={e.id}
                    data-testid={`event-chip-${e.id}`}
                    onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                    className={`block w-full truncate rounded border px-1.5 py-0.5 text-left text-[10px] leading-tight ${SCOPE_CHIP[e.scope] || SCOPE_CHIP.facility}`}
                    title={`${e.title}${e.city ? ` · ${e.city}` : ""}`}
                  >
                    {(e.clashes || []).length > 0 && <AlertTriangle className="mr-1 inline h-2.5 w-2.5 text-amber-400" />}
                    {e.start_time ? `${e.start_time} ` : ""}{e.title}
                  </button>
                ))}
                {evts.length > 3 && (
                  <div className="px-1 text-[10px] text-zinc-500" data-testid={`day-more-${iso}`}>+{evts.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ================= week view =================
function WeekView({ byDate, onEventClick }) {
  const days = weekDays(toISO(new Date()));
  return (
    <div className="card-surface overflow-hidden" data-testid="week-view">
      <div className="grid grid-cols-7 border-b border-[#1f1f1f] bg-[#0d0d0d]">
        {(days || []).map((d) => (
          <div key={toISO(d)} className="px-3 py-2 text-center">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className={`mt-0.5 font-display text-lg font-bold ${toISO(d) === toISO(new Date()) ? "text-emerald-400" : "text-zinc-200"}`}>{d.getDate()}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {(days || []).map((d) => {
          const iso = toISO(d);
          const evts = byDate[iso] || [];
          return (
            <div key={iso} className="min-h-[240px] border-r border-[#161616] p-1.5" data-testid={`week-day-${iso}`}>
              <div className="space-y-1">
                {evts.map((e) => (
                  <button
                    key={e.id}
                    data-testid={`event-chip-${e.id}`}
                    onClick={() => onEventClick(e)}
                    className={`block w-full rounded border px-2 py-1.5 text-left text-xs ${SCOPE_CHIP[e.scope] || SCOPE_CHIP.facility}`}
                  >
                    <div className="font-medium">{e.title}</div>
                    <div className="mt-0.5 text-[10px] opacity-80">
                      {e.start_time || "All day"}{e.city ? ` · ${e.city}` : ""}
                    </div>
                  </button>
                ))}
                {evts.length === 0 && <div className="px-1 pt-2 text-[10px] text-zinc-600">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ================= agenda view =================
function AgendaView({ events, onEventClick }) {
  if (events.length === 0) {
    return (
      <div className="card-surface p-10 text-center text-sm text-zinc-500" data-testid="agenda-empty">
        No events this month. Click <span className="text-zinc-300">New event</span> or tap a day on the month view.
      </div>
    );
  }
  const byDate = groupByDate(events);
  const dates = Object.keys(byDate).sort();
  return (
    <div className="space-y-3" data-testid="agenda-view">
      {(dates || []).map((d) => {
        const dt = new Date(d + "T00:00:00");
        return (
          <div key={d} className="card-surface p-4">
            <div className="mb-2 flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5 text-zinc-500" />
              <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">
                {dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
              </div>
            </div>
            <div className="space-y-1.5">
              {(byDate[d] || []).map((e) => (
                <button
                  key={e.id}
                  data-testid={`agenda-event-${e.id}`}
                  onClick={() => onEventClick(e)}
                  className="flex w-full items-center gap-3 rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3 py-2 text-left transition-colors hover:border-[#333]"
                >
                  <Badge variant="outline" className={`shrink-0 ${SCOPE_CHIP[e.scope] || SCOPE_CHIP.facility}`}>
                    {SCOPE_LABEL[e.scope] || "Facility"}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-100">{e.title}</div>
                    <div className="text-[11px] text-zinc-500">
                      {e.start_time ? `${e.start_time}${e.end_time ? `–${e.end_time}` : ""}` : "All day"}
                      {e.location ? ` · ${e.location}` : ""}
                      {e.city ? ` · ${e.city}` : ""}
                      {e.owner ? ` · ${e.owner}` : ""}
                    </div>
                  </div>
                  {(e.clashes || []).length > 0 && (
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" data-testid={`agenda-clash-${e.id}`} />
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ================= create / edit dialog =================
function EventDialog({ open, onOpenChange, initial, activeFacility, onSaved }) {
  const blank = {
    title: "", date: "", start_time: "", end_time: "", location: "",
    description: "", event_type: "", owner: "", budget: "", scope: "facility", city: "", in_app: false,
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [clashes, setClashes] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || "",
        date: initial?.date || "",
        start_time: initial?.start_time || "",
        end_time: initial?.end_time || "",
        location: initial?.location || "",
        description: initial?.description || "",
        event_type: initial?.event_type || "",
        owner: initial?.owner || "",
        budget: initial?.budget != null && initial?.budget !== 0 ? String(initial.budget) : "",
        scope: initial?.scope || "facility",
        city: initial?.city || "",
        in_app: !!initial?.in_app,
      });
      setClashes([]);
    }
  }, [open, initial]);

  // live clash preview
  useEffect(() => {
    if (!open || !form.date || !form.scope) { setClashes([]); return; }
    const t = setTimeout(() => {
      eventClashes({
        date: form.date,
        scope: form.scope,
        city: form.city || undefined,
        exclude_id: initial?.id,
      }).then((r) => setClashes(r.clashes || [])).catch(() => setClashes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [open, form.date, form.scope, form.city, initial?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    if (form.scope === "city" && !form.city.trim()) { toast.error("City is required for a city-wide event"); return; }
    setSaving(true);
    const payload = {
      ...form,
      title: form.title.trim(),
      city: form.city.trim() || null,
      budget: parseFloat(form.budget) || 0,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location || null,
      description: form.description || null,
      event_type: form.event_type || null,
      owner: form.owner || null,
    };
    try {
      let res;
      if (initial?.id) {
        res = await updateEvent(initial.id, payload);
        toast.success("Event updated");
      } else {
        res = await createEvent(payload);
        toast.success("Event created");
      }
      if (res?.has_clash) {
        toast.warning(`Heads up — this overlaps with: ${(res.clashes || []).map((c) => c.title).join(", ")}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription>
            Pan-LIFTR events show everywhere; city events show for all gyms in that city; facility events stay inside {activeFacility?.name || "this gym"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* scope */}
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Scope</div>
            <div className="grid grid-cols-3 gap-2" data-testid="event-scope-picker">
              {[
                { v: "facility", icon: Building2, label: "This gym" },
                { v: "city", icon: MapPin, label: "City-wide" },
                { v: "pan_liftr", icon: Globe2, label: "Pan LIFTR" },
              ].map(({ v, icon: Icon, label }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`dialog-scope-${v}`}
                  onClick={() => setForm((f) => ({
                    ...f,
                    scope: v,
                    // prefill city from the facility's details so city events auto-match this gym
                    city: v === "city" ? (f.city || activeFacility?.city || "") : f.city,
                  }))}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2.5 text-xs transition-colors ${
                    form.scope === v ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-[#262626] text-zinc-400 hover:border-[#333]"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          {form.scope === "city" && (
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">City</div>
              <Input data-testid="event-city" value={form.city} onChange={set("city")} placeholder="e.g. Chennai" />
              <p className="mt-1 text-[10px] text-zinc-600">
                Pre-filled from this facility&apos;s details (Global Admin → Edit facility). Gyms in this city will see the event.
              </p>
            </div>
          )}

          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Title *</div>
            <Input data-testid="event-title" value={form.title} onChange={set("title")} placeholder="e.g. LIFTR City Marathon" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Date *</div>
              <Input data-testid="event-date" type="date" value={form.date} onChange={set("date")} />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Start</div>
              <Input data-testid="event-start" type="time" value={form.start_time} onChange={set("start_time")} />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">End</div>
              <Input data-testid="event-end" type="time" value={form.end_time} onChange={set("end_time")} />
            </div>
          </div>

          {clashes.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-300" data-testid="event-clash-warning">
              <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Possible clash on this date</div>
              <ul className="mt-1 list-inside list-disc text-amber-200/80">
                {clashes.map((c) => (
                  <li key={c.id}>{c.title} <span className="text-amber-400/60">({SCOPE_LABEL[c.scope] || c.scope}{c.city ? ` · ${c.city}` : ""})</span></li>
                ))}
              </ul>
              <div className="mt-1 text-amber-400/60">You can still save — this is just a heads-up.</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Location</div>
              <Input data-testid="event-location" value={form.location} onChange={set("location")} placeholder="Venue / address" />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Type</div>
              <Input data-testid="event-type" value={form.event_type} onChange={set("event_type")} placeholder="Marathon, Workshop…" />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Owner / Organiser</div>
              <Input data-testid="event-owner" value={form.owner} onChange={set("owner")} placeholder="Who's running it" />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Budget</div>
              <Input data-testid="event-budget" type="number" value={form.budget} onChange={set("budget")} placeholder="0" />
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Description</div>
            <textarea
              data-testid="event-description"
              value={form.description}
              onChange={set("description")}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Notes, registration link, what to bring…"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              data-testid="event-in-app"
              checked={form.in_app}
              onChange={(e) => setForm((f) => ({ ...f, in_app: e.target.checked }))}
              className="mt-0.5 h-4 w-4 accent-red-600"
            />
            <span>
              Show in the member app
              <span className="block text-xs text-zinc-500">Members see it under My Gym, in what's coming up.</span>
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" data-testid="event-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="event-save" onClick={save} disabled={saving}>
              {saving ? "Saving…" : initial?.id ? "Save changes" : "Create event"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ================= detail dialog =================
function EventDetail({ event, onClose, onEdit, onDeleted }) {
  const askDelete = useDeleteAsk();
  if (!event) return null;
  const del = async () => {
    const reason = askDelete(`"${event.title}"`, `Delete "${event.title}"?`);
    if (reason === null) return;
    try {
      if (deletionRequested(await deleteEvent(event.id, reason))) return;
      toast.success("Event deleted");
      onDeleted();
    } catch {
      toast.error("Could not delete event");
    }
  };
  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md" data-testid="event-detail">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={SCOPE_CHIP[event.scope] || SCOPE_CHIP.facility}>
              {SCOPE_LABEL[event.scope] || "Facility"}
            </Badge>
            {(event.clashes || []).length > 0 && (
              <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-300" data-testid="detail-clash-badge">
                <AlertTriangle className="mr-1 h-3 w-3" /> {event.clashes.length} overlap{event.clashes.length === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
          <DialogTitle className="mt-1">{event.title}</DialogTitle>
          <DialogDescription>
            {new Date(event.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            {event.start_time ? ` · ${event.start_time}${event.end_time ? `–${event.end_time}` : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {event.city && <Row label="City" value={event.city} />}
          {event.location && <Row label="Location" value={event.location} />}
          {event.event_type && <Row label="Type" value={event.event_type} />}
          {event.owner && <Row label="Owner" value={event.owner} />}
          {!!event.budget && <Row label="Budget" value={String(event.budget)} />}
          {event.in_app && <Row label="Member app" value="Shown under My Gym" />}
          {event.description && <Row label="Notes" value={event.description} />}
          {event.created_by_name && <Row label="Added by" value={event.created_by_name} />}
        </div>
        {(event.clashes || []).length > 0 && (
          <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-300" data-testid="detail-clashes">
            Overlaps with: {(event.clashes || []).map((c) => c.title).join(", ")}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" data-testid="event-delete-btn" onClick={del}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
          </Button>
          <Button size="sm" data-testid="event-edit-btn" onClick={() => onEdit(event)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3">
      <div className="w-20 shrink-0 text-[10px] uppercase tracking-[0.18em] text-zinc-500 pt-0.5">{label}</div>
      <div className="text-zinc-200">{value}</div>
    </div>
  );
}
